import React, { useRef, useState, useEffect, useCallback } from 'react';
import { FilmIcon } from '../icons/FilmIcon';
import { MusicIcon } from '../icons/MusicIcon';
import { SoundIcon } from '../icons/SoundIcon';
import { TextIcon } from '../icons/TextIcon';
import { TrashIcon } from '../icons/TrashIcon';
import { SelectionIcon } from '../icons/SelectionIcon';
import { SplitIcon } from '../icons/SplitIcon';
import { PhotoIcon } from '../icons/PhotoIcon';

const PIXELS_PER_SECOND = 50;
const MIN_CLIP_DURATION = 0.1;

const TimelineToolbar = ({ activeTool, setActiveTool }) => {
    const tools = [
        { name: 'select', icon: SelectionIcon },
        { name: 'split', icon: SplitIcon },
    ];

    return (
        <div className="h-full flex items-center px-2 space-x-1 bg-[#1f1f29] border-b border-r border-gray-700">
            {tools.map(tool => (
                <button 
                    key={tool.name}
                    onClick={() => setActiveTool(tool.name)}
                    className={`p-2 rounded-md transition-colors ${activeTool === tool.name ? 'bg-purple-600 text-white' : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
                    aria-label={`Activate ${tool.name} tool`}
                >
                    <tool.icon className="w-5 h-5" />
                </button>
            ))}
        </div>
    );
};

export const TimelineEditor = ({ 
    project, playheadPosition, setPlayheadPosition, onClipSelected, 
    selectedClipId, onUpdateClipInProgress, onUpdateClipComplete, onDeleteClip, isPlaying,
    activeTool, setActiveTool, onSplitClip
}) => {
    const timeline = project.timeline;
    const scrollContainerRef = useRef(null);

    const [dragState, setDragState] = useState(null);
    
    const handleMouseDown = useCallback((e, clip, mode) => {
        if (activeTool !== 'select') return;
        e.stopPropagation();
        onClipSelected(clip);
        
        const sourceStart = (clip.type === 'video') ? clip.sourceStart || 0 : 0;

        setDragState({
            clip,
            mode,
            initialMouseX: e.clientX,
            initialStart: clip.start,
            initialDuration: clip.duration,
            initialSourceStart: sourceStart
        });
    }, [onClipSelected, activeTool]);

    const renderRulerMarkers = useCallback(() => {
        if (!timeline) return null;

        const markers = [];
        const totalDuration = timeline.duration;
        const majorTickSeconds = 1;
        const minorTicksPerMajor = 5;
        const majorTickCount = Math.ceil(totalDuration / majorTickSeconds);

        for (let i = 0; i <= majorTickCount; i++) {
            const time = i * majorTickSeconds;
            const left = time * PIXELS_PER_SECOND;

            markers.push(
                <div key={`major-${i}`} style={{ left: left - 0.5 }} className="absolute top-0 h-full">
                    <div className="w-px h-full bg-gray-600"></div>
                    <span className="absolute top-0 -translate-x-1/2 text-xs text-gray-500 mt-1">
                        {new Date(time * 1000).toISOString().substring(14, 19)}
                    </span>
                </div>
            );

            if (i < majorTickCount) {
                 for (let j = 1; j < minorTicksPerMajor; j++) {
                    const minorTime = time + j * (majorTickSeconds / minorTicksPerMajor);
                    if (minorTime >= totalDuration) break;
                    const minorLeft = minorTime * PIXELS_PER_SECOND;
                    markers.push(
                        <div key={`minor-${minorTime}`} style={{ left: minorLeft - 0.5 }} className="absolute top-1/2 h-1/2 w-px bg-gray-700" />
                    );
                }
            }
        }
        return markers;
    }, [timeline]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragState) return;

            const deltaX = e.clientX - dragState.initialMouseX;
            const deltaTime = deltaX / PIXELS_PER_SECOND;

            let newStart = dragState.initialStart;
            let newDuration = dragState.initialDuration;
            let newSourceStart = dragState.initialSourceStart;

            if (dragState.mode === 'move') {
                newStart = Math.max(0, dragState.initialStart + deltaTime);
            } else if (dragState.mode === 'trim-start') {
                const startDelta = Math.min(Math.max(deltaTime, -dragState.initialStart), dragState.initialDuration - MIN_CLIP_DURATION);
                newStart = dragState.initialStart + startDelta;
                newDuration = dragState.initialDuration - startDelta;
                if (dragState.clip.type === 'video') {
                    newSourceStart = Math.max(0, dragState.initialSourceStart + startDelta);
                }
            } else {
                newDuration = Math.max(MIN_CLIP_DURATION, dragState.initialDuration + deltaTime);
            }

            const updatedClip = { 
                ...dragState.clip, 
                start: parseFloat(newStart.toFixed(3)), 
                duration: parseFloat(newDuration.toFixed(3)) 
            };

            if (updatedClip.type === 'video') {
                updatedClip.sourceStart = parseFloat(newSourceStart.toFixed(3));
            }

            onUpdateClipInProgress(updatedClip);
        };

        const handleMouseUp = () => {
            if (dragState) {
                const finalClip = project.timeline?.clips.find(c => c.id === dragState.clip.id);
                if (finalClip) {
                    onUpdateClipComplete(finalClip);
                }
            }
            setDragState(null);
        };

        if (dragState) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp, { once: true });
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragState, onUpdateClipInProgress, onUpdateClipComplete, project.timeline?.clips]);
    
    useEffect(() => {
        if (!isPlaying || !scrollContainerRef.current) return;

        const container = scrollContainerRef.current;
        const playheadPixelPos = playheadPosition * PIXELS_PER_SECOND;
        const { scrollLeft, clientWidth } = container;
        
        const scrollMargin = 100;
        if (playheadPixelPos < scrollLeft + scrollMargin || playheadPixelPos > scrollLeft + clientWidth - scrollMargin) {
             container.scrollTo({ 
                left: playheadPixelPos - clientWidth / 2, 
                behavior: 'smooth' 
            });
        }
    }, [playheadPosition, isPlaying]);

    if (!timeline) {
        return <div className="h-full flex items-center justify-center text-gray-500">No timeline data available.</div>;
    }
    
    const timelineWidth = timeline.duration * PIXELS_PER_SECOND;

    const handleTimelineClick = (e) => {
        const scrollEl = scrollContainerRef.current;
        if (!scrollEl) return;
        const rect = scrollEl.getBoundingClientRect();
        const scrollLeft = scrollEl.scrollLeft;
        const x = e.clientX - rect.left + scrollLeft;
        
        const clickTime = x / PIXELS_PER_SECOND;
        setPlayheadPosition(Math.max(0, Math.min(timeline.duration, clickTime)));

        if (activeTool === 'split') {
            const trackIndex = Math.floor((e.clientY - rect.top - 32) / 64);
            if (trackIndex >= 0 && trackIndex < timeline.tracks.length) {
                const trackId = timeline.tracks[trackIndex].id;
                const clipToSplit = timeline.clips.find(c => c.trackId === trackId && clickTime >= c.start && clickTime < c.start + c.duration);
                if (clipToSplit) {
                    onSplitClip(clipToSplit.id, clickTime);
                }
            }
        } else {
             onClipSelected(null);
        }
    };

    const renderClip = (clip) => {
        const left = clip.start * PIXELS_PER_SECOND;
        const width = clip.duration * PIXELS_PER_SECOND;
        const isSelected = clip.id === selectedClipId && activeTool === 'select';

        let bgColor = 'bg-gray-600';
        let content;
        let cursorStyle = 'default';

        if (activeTool === 'select') {
             cursorStyle = dragState?.mode === 'move' ? 'grabbing' : 'grab';
        } else if (activeTool === 'split') {
            cursorStyle = 'pointer';
        }

        switch(clip.type) {
            case 'video':
                const shot = project.scenes.flatMap(s => s.shots).find(s => s.id === clip.shotId);
                const hasVideo = !!shot?.video;
                
                bgColor = hasVideo ? 'bg-blue-600/80 hover:bg-blue-600' : 'bg-indigo-600/80 hover:bg-indigo-600';
                
                content = (
                    <>
                        {hasVideo ? <FilmIcon className="w-4 h-4 mr-2 flex-shrink-0" /> : <PhotoIcon className="w-4 h-4 mr-2 flex-shrink-0" />}
                        <span className="text-xs truncate">{shot?.description || 'Visual Clip'}</span>
                    </>
                );
                break;
            case 'audio':
                const track = timeline.tracks.find(t => t.id === clip.trackId);
                bgColor = track?.name === 'Music' ? 'bg-green-600/80 hover:bg-green-600' : 'bg-teal-600/80 hover:bg-teal-600';
                content = (
                    <>
                        {track?.name === 'Music' ? <MusicIcon className="w-4 h-4 mr-2 flex-shrink-0" /> : <SoundIcon className="w-4 h-4 mr-2 flex-shrink-0" />}
                        <span className="text-xs truncate">{clip.name || 'Audio Clip'}</span>
                    </>
                );
                break;
            case 'text':
                bgColor = 'bg-purple-600/80 hover:bg-purple-600';
                content = (
                     <>
                        <TextIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="text-xs truncate">{clip.content || 'Text Clip'}</span>
                    </>
                );
                break;
        }

        return (
            <div
                key={clip.id}
                className={`absolute h-10 top-1/2 -translate-y-1/2 rounded-md text-white overflow-hidden transition-colors ${bgColor} ${isSelected ? 'ring-2 ring-yellow-400 z-10' : 'border border-black/20'}`}
                style={{ left, width }}
                 onMouseDown={(e) => handleMouseDown(e, clip, 'move')}
            >
                <div 
                    className="w-full h-full flex items-center px-2"
                    style={{ cursor: cursorStyle }}
                >
                     {content}
                </div>

                {isSelected && (
                    <>
                        <div
                            className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize z-20 flex items-center justify-center bg-black/20 hover:bg-white/20"
                            onMouseDown={(e) => handleMouseDown(e, clip, 'trim-start')}
                            title="Trim Start (Slip Edit for Video)"
                        >
                            <div className="w-1 h-4 bg-yellow-400 rounded-full" />
                        </div>
                        <div
                            className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize z-20 flex items-center justify-center bg-black/20 hover:bg-white/20"
                            onMouseDown={(e) => handleMouseDown(e, clip, 'trim-end')}
                            title="Trim End"
                        >
                             <div className="w-1 h-4 bg-yellow-400 rounded-full" />
                        </div>
                        <button
                            className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-red-500 z-20"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteClip(clip.id);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            aria-label="Delete clip"
                        >
                            <TrashIcon className="w-3 h-3"/>
                        </button>
                    </>
                )}
            </div>
        );
    };
    
    const getTrackIcon = (track) => {
        let IconComponent;
        switch (track.type) {
            case 'video': IconComponent = FilmIcon; break;
            case 'audio': IconComponent = track.name === 'Music' ? MusicIcon : SoundIcon; break;
            case 'text': IconComponent = TextIcon; break;
            default: IconComponent = () => null;
        }
        return IconComponent;
    };

    const getTimelineCursor = () => {
        if (activeTool === 'split') return 'pointer';
        return 'default';
    }

    return (
        <div className="h-full w-full bg-[#181820] flex overflow-hidden">
            <div className="w-48 flex-shrink-0 bg-[#1f1f29] border-r border-gray-700 z-40 flex flex-col">
                <div className="h-8 shrink-0">
                    <TimelineToolbar activeTool={activeTool} setActiveTool={setActiveTool} />
                </div>
                <div className="flex-grow">
                    {timeline.tracks.map(track => {
                        const IconComponent = getTrackIcon(track);
                        return (
                            <div key={track.id} className="h-16 border-b border-gray-700 flex items-center p-4">
                                <IconComponent className="w-5 h-5 mr-3 text-gray-400" />
                                <span className="font-semibold">{track.name}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div ref={scrollContainerRef} className="flex-grow overflow-auto relative" onClick={handleTimelineClick} style={{ cursor: getTimelineCursor() }}>
                <div className="relative" style={{ width: Math.max(1, timelineWidth), minHeight: '100%' }}>
                    <div className="h-8 bg-[#1f1f29] border-b border-gray-700 sticky top-0 z-30">
                        <div className="relative w-full h-full">
                            {renderRulerMarkers()}
                        </div>
                    </div>

                    <div className="relative">
                        {timeline.tracks.map(track => (
                            <div key={track.id} className="relative h-16 border-b border-gray-700 bg-gray-800/50">
                                {timeline.clips.filter(c => c.trackId === track.id).map(renderClip)}
                            </div>
                        ))}
                    </div>

                    <div
                        className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20 pointer-events-none"
                        style={{ left: playheadPosition * PIXELS_PER_SECOND }}
                    >
                        <div className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-red-500 rounded-full border-2 border-[#1f1f29]"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};
